// frontend/src/pages/Messages.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';

const CHANNELS = ['all', 'ebay_source', 'ebay_store2', 'ebay_store3'];
const CHANNEL_LABELS = {
  all:          'All',
  ebay_source:  'Euphoric Operations',
  ebay_store2:  'TNT Operations',
  ebay_store3:  'Firestarters',
  poshmark:     'Poshmark',
  mercari:      'Mercari',
};
const CHANNEL_BG = {
  ebay_source:  'linear-gradient(135deg, #6c63ff, #8b5cf6)',
  ebay_store2:  'linear-gradient(135deg, #00d4aa, #06b6d4)',
  ebay_store3:  'linear-gradient(135deg, #f97316, #ef4444)',
  poshmark:     'linear-gradient(135deg, #ec4899, #db2777)',
  mercari:      'linear-gradient(135deg, #fbbf24, #f59e0b)',
};

const QUICK_REPLIES = [
  { label: '👍 Thanks',    text: 'Thank you for your purchase! Your order will ship within 1 business day.' },
  { label: '📦 Has box',   text: 'Yes, this item will include the original box.' },
  { label: '✅ Shipped',   text: 'Your order has shipped! Tracking should update within 24 hours.' },
  { label: '🔍 Looking',   text: 'Thanks for reaching out — let me look into this and get back to you shortly.' },
];

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+|[_-]/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return 'Yesterday';
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fullTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function Avatar({ name, channel, size = 40 }) {
  const bg = CHANNEL_BG[channel] || 'linear-gradient(135deg, #6c63ff, #8b5cf6)';
  return (
    <div className="msg-avatar" style={{
      width: size, height: size, background: bg,
      fontSize: Math.round(size * 0.36),
    }}>
      {initials(name)}
    </div>
  );
}

export default function Messages({ setUnread }) {
  const [messages, setMessages]         = useState([]);
  const [unreadCounts, setUnreadCounts] = useState([]);
  const [channel, setChannel]           = useState('all');
  const [unreadOnly, setUnreadOnly]     = useState(false);
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [replyText, setReplyText]       = useState('');
  const [replying, setReplying]         = useState(false);
  const [replyStatus, setReplyStatus]   = useState('');
  const [syncing, setSyncing]           = useState(false);
  const [restocking, setRestocking]     = useState(false);
  const [restockResult, setRestockResult] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const replyRef = useRef(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (channel !== 'all') params.set('channel', channel);
    if (unreadOnly) params.set('unread', 'true');
    api.get(`/messages?${params}`).then(r => {
      setMessages(r.data.messages || []);
      setUnreadCounts(r.data.unreadCounts || []);
      const total = (r.data.unreadCounts || []).reduce((a, b) => a + b.count, 0);
      setUnread(total);
    });
  }, [channel, unreadOnly, setUnread]);

  useEffect(() => { load(); }, [load]);

  const filtered = messages.filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (m.buyer_name || '').toLowerCase().includes(q) ||
      (m.subject || '').toLowerCase().includes(q) ||
      (m.body || '').toLowerCase().includes(q) ||
      (m.sku || '').toLowerCase().includes(q) ||
      (m.order_id || '').toLowerCase().includes(q)
    );
  });

  const openMessage = useCallback((msg) => {
    setSelected(msg);
    setReplyText('');
    setReplyStatus('');
    setRestockResult(null);
    if (!msg.is_read) {
      api.patch(`/messages/${msg.id}/read`).then(() => {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: 1 } : m));
        setUnreadCounts(prev => prev.map(c => c.channel === msg.channel ? { ...c, count: Math.max(0, c.count - 1) } : c));
      });
    }
  }, []);

  async function sendReply() {
    if (!replyText.trim()) return;
    setReplying(true);
    setReplyStatus('');
    try {
      await api.post(`/messages/${selected.id}/reply`, { text: replyText });
      const now = new Date().toISOString();
      setMessages(prev => prev.map(m => m.id === selected.id ? { ...m, replied_at: now } : m));
      setSelected(prev => prev ? { ...prev, replied_at: now } : prev);
      setReplyStatus('sent');
      setReplyText('');
      setTimeout(() => setReplyStatus(''), 3000);
    } catch (err) {
      setReplyStatus('error: ' + (err.response?.data?.error || err.message));
    } finally {
      setReplying(false);
    }
  }

  async function syncMessages() {
    setSyncing(true);
    await api.post('/sync/messages').catch(() => {});
    setSyncing(false);
    load();
  }

  async function restockFromReturn() {
    if (!selected?.sku) return;
    if (!window.confirm(`Add 1 unit of ${selected.sku} back to inventory?`)) return;
    setRestocking(true);
    setRestockResult(null);
    try {
      const res = await api.post(`/messages/${selected.id}/restock`, { quantity: 1 });
      setRestockResult({ ok: true, sku: res.data.sku, newQty: res.data.newQty });
    } catch (err) {
      setRestockResult({ ok: false, error: err.response?.data?.error || err.message });
    }
    setRestocking(false);
  }

  async function deleteMessage() {
    if (!selected) return;
    if (!window.confirm('Delete this message?')) return;
    setDeleting(true);
    try {
      await api.delete(`/messages/${selected.id}`);
      setMessages(prev => prev.filter(m => m.id !== selected.id));
      setSelected(null);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
    setDeleting(false);
  }

  // Keyboard shortcuts: Esc closes, / focuses search, j/k navigates
  useEffect(() => {
    function onKey(e) {
      const tag = document.activeElement?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA';
      if (e.key === 'Escape' && selected) {
        if (!isTyping) setSelected(null);
        return;
      }
      if (isTyping) return;
      if (e.key === '/') {
        e.preventDefault();
        document.getElementById('msg-search')?.focus();
        return;
      }
      if (e.key === 'j' || e.key === 'k') {
        if (filtered.length === 0) return;
        const idx = selected ? filtered.findIndex(m => m.id === selected.id) : -1;
        const next = e.key === 'j'
          ? Math.min(filtered.length - 1, idx + 1)
          : Math.max(0, idx - 1);
        if (filtered[next]) openMessage(filtered[next]);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered, selected, openMessage]);

  function onReplyKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      sendReply();
    }
  }

  function appendQuickReply(text) {
    setReplyText(prev => prev.trim() ? prev.trim() + ' ' + text : text);
    setTimeout(() => replyRef.current?.focus(), 0);
  }

  const canReply = selected && ['ebay_source', 'ebay_store2', 'ebay_store3'].includes(selected.channel);
  const totalUnread = unreadCounts.reduce((a, b) => a + b.count, 0);

  return (
    <div className="msg-shell">
      <div className="page-header" style={{ marginBottom: 12 }}>
        <div className="page-title">
          💬 Messages
          {totalUnread > 0 && <span className="badge badge-pending" style={{ fontSize: 13, marginLeft: 8 }}>{totalUnread} unread</span>}
        </div>
        <button className="btn btn-ghost" onClick={syncMessages} disabled={syncing}>
          {syncing ? '⏳ Syncing…' : '🔄 Sync'}
        </button>
      </div>

      <div className={`msg-pane ${selected ? 'has-selection' : ''}`}>
        {/* INBOX */}
        <div className="msg-list">
          <div className="msg-search-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', pointerEvents:'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              id="msg-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search buyer, subject, SKU…"
              className="msg-search-input"
            />
            {search && (
              <button className="msg-search-clear" onClick={() => setSearch('')} title="Clear">✕</button>
            )}
          </div>

          <div className="msg-filters">
            {CHANNELS.map(c => {
              const cnt = c !== 'all' ? unreadCounts.find(u => u.channel === c)?.count || 0 : 0;
              return (
                <button key={c}
                  className={`filter-tab ${channel === c ? 'active' : ''}`}
                  onClick={() => setChannel(c)}>
                  {CHANNEL_LABELS[c] || c}
                  {cnt > 0 && <span className="msg-filter-count">{cnt}</span>}
                </button>
              );
            })}
            <label className="msg-unread-toggle">
              <input type="checkbox" checked={unreadOnly} onChange={e => setUnreadOnly(e.target.checked)} />
              Unread only
            </label>
          </div>

          {filtered.length === 0 ? (
            <div className="msg-empty">
              <div style={{ fontSize: 48, opacity: 0.5 }}>📭</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>
                {search ? 'No matches' : 'No messages'}
              </div>
              {!search && (
                <button className="btn btn-primary" onClick={syncMessages} disabled={syncing} style={{ marginTop: 12 }}>
                  {syncing ? 'Syncing…' : 'Sync now'}
                </button>
              )}
            </div>
          ) : (
            <div className="msg-cards">
              {filtered.map(msg => (
                <button key={msg.id}
                  className={`msg-card ${!msg.is_read ? 'unread' : ''} ${selected?.id === msg.id ? 'selected' : ''} ${msg.replied_at ? 'replied' : ''}`}
                  onClick={() => openMessage(msg)}>
                  <Avatar name={msg.buyer_name} channel={msg.channel} size={40} />
                  <div className="msg-card-content">
                    <div className="msg-card-line1">
                      <span className="msg-card-name">{msg.buyer_name || 'Unknown buyer'}</span>
                      <span className="msg-card-time">{relativeTime(msg.received_at)}</span>
                    </div>
                    <div className="msg-card-subject">
                      {msg.replied_at && <span className="msg-replied-tick" title={`Replied ${fullTime(msg.replied_at)}`}>✓</span>}
                      {msg.subject || '(no subject)'}
                    </div>
                    <div className="msg-card-preview">{msg.body || ''}</div>
                    <div className="msg-card-footer">
                      <span className="msg-card-channel">{CHANNEL_LABELS[msg.channel] || msg.channel}</span>
                      {msg.sku && <span className="msg-card-sku">📦 {msg.sku}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAIL */}
        {selected && (
          <div className="msg-detail">
            <div className="msg-detail-header">
              <button className="msg-back" onClick={() => setSelected(null)} title="Back / Close (Esc)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              <Avatar name={selected.buyer_name} channel={selected.channel} size={44} />
              <div className="msg-detail-who">
                <div className="msg-detail-name">{selected.buyer_name || 'Unknown buyer'}</div>
                <div className="msg-detail-channel">
                  {CHANNEL_LABELS[selected.channel] || selected.channel}
                  {selected.replied_at && <span style={{ color: 'var(--accent2)', marginLeft: 8 }}>· ✓ Replied {relativeTime(selected.replied_at)}</span>}
                </div>
              </div>
              <button onClick={deleteMessage} disabled={deleting} className="msg-delete" title="Delete message">
                {deleting ? '...' : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                )}
              </button>
            </div>

            <div className="msg-detail-body">
              <div className="msg-detail-subject">{selected.subject}</div>
              <div className="msg-detail-meta">{fullTime(selected.received_at)}</div>

              {selected.sku && (
                <div className="msg-context-card">
                  <div className="msg-context-row">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="msg-context-label">SKU</div>
                      <div>
                        <span className="msg-context-sku">{selected.sku}</span>
                        {selected.product_title && <span className="msg-context-title">{selected.product_title}</span>}
                      </div>
                    </div>
                    <button onClick={restockFromReturn} disabled={restocking} className="msg-context-btn">
                      {restocking ? '...' : '+ Restock'}
                    </button>
                  </div>
                  {restockResult?.ok && (
                    <div className="msg-context-result success">Added 1 unit (now {restockResult.newQty} in stock)</div>
                  )}
                  {restockResult && !restockResult.ok && (
                    <div className="msg-context-result error">{restockResult.error}</div>
                  )}
                </div>
              )}

              {selected.order_id && (
                <Link to={`/orders?search=${encodeURIComponent(selected.order_id)}`} className="msg-order-link">
                  📋 Order: {selected.order_id} →
                </Link>
              )}

              <div className="msg-detail-text">{selected.body}</div>
            </div>

            {canReply ? (
              <div className="msg-detail-footer">
                <div className="msg-quick-replies">
                  {QUICK_REPLIES.map(q => (
                    <button key={q.label} className="msg-quick-reply"
                      onClick={() => appendQuickReply(q.text)}
                      title={q.text}>{q.label}</button>
                  ))}
                </div>
                <div className="msg-reply-row">
                  <textarea
                    ref={replyRef}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={onReplyKeyDown}
                    placeholder="Type your reply…   (⌘/Ctrl+Enter to send)"
                  />
                  <button className="btn btn-primary msg-send-btn"
                    onClick={sendReply}
                    disabled={replying || !replyText.trim()}>
                    {replying ? '⏳' : '📤'}
                  </button>
                </div>
                {replyStatus === 'sent' && <div className="success-text" style={{ marginTop: 6 }}>✓ Sent</div>}
                {replyStatus.startsWith('error') && <div className="error-text" style={{ marginTop: 6, fontSize: 12 }}>{replyStatus}</div>}
              </div>
            ) : (
              <div className="msg-detail-footer" style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                Reply not available for {CHANNEL_LABELS[selected.channel] || selected.channel}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
